import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import type { SpeedPoint } from '../lib/api.js';
import { formatearTamano } from '../lib/eventos.js';

/** Los dos colores de la gráfica, fijos: la bajada es SIEMPRE azul y la subida naranja, aquí
 *  y en cualquier otra pantalla que las pinte. Validados sobre el fondo oscuro del tema para
 *  daltonismo y contraste (ΔE 26,8 en protanopía, ≥ 3:1 sobre el papel). */
export const COLOR_BAJADA = '#3987e5';
export const COLOR_SUBIDA = '#d95926';

const ALTO = 240;
const MARGEN = { arriba: 14, derecha: 16, abajo: 28, izquierda: 68 };

/** El ancho real del contenedor, para dibujar en píxeles y no estirar el texto con un
 *  `viewBox`. */
function useAncho(ref: React.RefObject<HTMLDivElement | null>): number {
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setAncho(e?.contentRect.width ?? 0));
    ro.observe(el);
    setAncho(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [ref]);
  return ancho;
}

const UNIDADES = ['B', 'KB', 'MB', 'GB'];

/** Un eje con números redondos: el tope y sus marcas, en una sola unidad para toda la
 *  escala. Se elige el paso más pequeño que deja como mucho cuatro marcas. */
function escalaBonita(max: number): { tope: number; marcas: number[]; unidad: number; nombre: string } {
  const minimo = 10 * 1024; // 10 KB/s: con todo a cero la gráfica sigue teniendo eje
  const m = Math.max(max, minimo);
  const k = Math.min(UNIDADES.length - 1, Math.floor(Math.log(m) / Math.log(1024)));
  const unidad = 1024 ** k;
  const enUnidad = m / unidad;
  const pasos = [0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100, 200, 250, 500];
  const paso = pasos.find((p) => enUnidad / p <= 4) ?? 500;
  const cuantas = Math.max(1, Math.ceil(enUnidad / paso));
  return {
    tope: cuantas * paso * unidad,
    marcas: Array.from({ length: cuantas + 1 }, (_, i) => i * paso * unidad),
    unidad,
    nombre: UNIDADES[k] ?? 'B',
  };
}

function etiquetaMarca(bps: number, unidad: number, nombre: string): string {
  const v = Number((bps / unidad).toFixed(2));
  return `${v} ${nombre}/s`;
}

function hora(unix: number, conDia: boolean): string {
  const d = new Date(unix * 1000);
  const hm = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return conDia ? `${d.toLocaleDateString('es-ES', { weekday: 'short' })} ${hm}` : hm;
}

const velocidad = (bps: number) => `${formatearTamano(bps)}/s`;

interface Props {
  puntos: SpeedPoint[];
  /** Cuánto abarca la gráfica, para decidir si las horas del eje llevan el día. */
  ventanaSegundos: number;
  /** Mientras se recarga se deja lo de antes, atenuado: ni parpadeo ni salto de altura. */
  atenuada?: boolean;
}

/** Velocidad de bajada y de subida en el tiempo: dos líneas sobre UN solo eje, que son la
 *  misma magnitud. Con el puntero encima, una guía vertical y un rótulo con las dos
 *  velocidades de ese instante; con el teclado, las flechas recorren los puntos. Y la misma
 *  serie como tabla, para quien no pueda o no quiera leer la línea. */
export function GraficaVelocidad({ puntos, ventanaSegundos, atenuada = false }: Props) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement | null>(null);
  const ancho = useAncho(ref);
  const [indice, setIndice] = useState<number | null>(null);
  const [comoTabla, setComoTabla] = useState(false);

  const plot = {
    x0: MARGEN.izquierda,
    x1: Math.max(MARGEN.izquierda + 40, ancho - MARGEN.derecha),
    y0: MARGEN.arriba,
    y1: ALTO - MARGEN.abajo,
  };

  const escala = useMemo(
    () => escalaBonita(Math.max(0, ...puntos.map((p) => Math.max(p.downloadBps, p.uploadBps)))),
    [puntos],
  );

  const n = puntos.length;
  const x = (i: number) => (n < 2 ? plot.x0 : plot.x0 + ((plot.x1 - plot.x0) * i) / (n - 1));
  const y = (bps: number) => plot.y1 - ((plot.y1 - plot.y0) * bps) / escala.tope;
  const camino = (de: (p: SpeedPoint) => number) =>
    puntos.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(de(p)).toFixed(1)}`).join(' ');

  const conDia = ventanaSegundos >= 86_400;
  // Tantas horas en el eje como quepan sin pisarse: en un móvil son dos, no cuatro.
  const cuantasX = Math.max(2, Math.min(4, Math.floor((plot.x1 - plot.x0) / (conDia ? 110 : 80))));
  const marcasX = n < 2
    ? []
    : Array.from({ length: cuantasX }, (_, i) => Math.round(((n - 1) * i) / (cuantasX - 1)));

  const elegirPorX = (px: number) => {
    if (n < 2) return;
    const rel = (px - plot.x0) / (plot.x1 - plot.x0);
    setIndice(Math.max(0, Math.min(n - 1, Math.round(rel * (n - 1)))));
  };

  const ultimo = puntos.at(-1);
  const sel = indice != null ? puntos[indice] : undefined;
  const superficie = theme.palette.background.paper;
  const rejilla = theme.palette.divider;
  const texto = theme.palette.text.secondary;

  return (
    <Box ref={ref} sx={{ position: 'relative', opacity: atenuada ? 0.6 : 1, transition: 'opacity .2s' }}>
      <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Leyenda color={COLOR_BAJADA} nombre="Bajada" valor={ultimo ? velocidad(ultimo.downloadBps) : '—'} />
        <Leyenda color={COLOR_SUBIDA} nombre="Subida" valor={ultimo ? velocidad(ultimo.uploadBps) : '—'} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="inherit" onClick={() => setComoTabla((v) => !v)} sx={{ color: 'text.secondary' }}>
          {comoTabla ? 'Ver gráfica' : 'Ver como tabla'}
        </Button>
      </Stack>

      {n < 2 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          aMule todavía no tiene historia que enseñar: acaba de arrancar, o la ventana pedida
          es más larga que lo que guarda.
        </Typography>
      ) : comoTabla ? (
        <TablaPuntos puntos={puntos} conDia={conDia} />
      ) : (
        <>
          <svg
            width="100%"
            height={ALTO}
            role="img"
            aria-label="Velocidad de bajada y de subida en el tiempo"
            tabIndex={0}
            style={{ display: 'block', outline: 'none', touchAction: 'none' }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const paso = e.key === 'ArrowLeft' ? -1 : 1;
                setIndice((i) => Math.max(0, Math.min(n - 1, (i ?? n - 1) + paso)));
              }
              if (e.key === 'Escape') setIndice(null);
            }}
            onBlur={() => setIndice(null)}
          >
            {/* Rejilla: un pelo, sin rayas discontinuas, un tono por encima del fondo. */}
            {escala.marcas.map((m) => (
              <g key={m}>
                <line x1={plot.x0} x2={plot.x1} y1={y(m)} y2={y(m)} stroke={rejilla} strokeWidth={1} />
                <text x={plot.x0 - 8} y={y(m) + 4} textAnchor="end" fontSize={11} fill={texto}>
                  {etiquetaMarca(m, escala.unidad, escala.nombre)}
                </text>
              </g>
            ))}
            {marcasX.map((i) => (
              <text
                key={i}
                x={x(i)}
                y={ALTO - 8}
                textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
                fontSize={11}
                fill={texto}
              >
                {hora(puntos[i]!.t, conDia)}
              </text>
            ))}

            <path d={camino((p) => p.downloadBps)} fill="none" stroke={COLOR_BAJADA} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            <path d={camino((p) => p.uploadBps)} fill="none" stroke={COLOR_SUBIDA} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

            {/* El punto final de cada serie, con un anillo del color del fondo para que se
                distinga donde las dos líneas se cruzan. */}
            {ultimo && (
              <>
                <circle cx={x(n - 1)} cy={y(ultimo.downloadBps)} r={4} fill={COLOR_BAJADA} stroke={superficie} strokeWidth={2} />
                <circle cx={x(n - 1)} cy={y(ultimo.uploadBps)} r={4} fill={COLOR_SUBIDA} stroke={superficie} strokeWidth={2} />
              </>
            )}

            {sel && indice != null && (
              <g pointerEvents="none">
                <line x1={x(indice)} x2={x(indice)} y1={plot.y0} y2={plot.y1} stroke={texto} strokeWidth={1} />
                <circle cx={x(indice)} cy={y(sel.downloadBps)} r={4} fill={COLOR_BAJADA} stroke={superficie} strokeWidth={2} />
                <circle cx={x(indice)} cy={y(sel.uploadBps)} r={4} fill={COLOR_SUBIDA} stroke={superficie} strokeWidth={2} />
              </g>
            )}

            {/* La zona sensible es TODO el área de dibujo: se apunta a un instante, no a una
                línea de dos píxeles. */}
            <rect
              x={plot.x0}
              y={plot.y0}
              width={plot.x1 - plot.x0}
              height={plot.y1 - plot.y0}
              fill="transparent"
              onPointerMove={(e) => elegirPorX(e.nativeEvent.offsetX)}
              onPointerDown={(e) => elegirPorX(e.nativeEvent.offsetX)}
              onPointerLeave={() => setIndice(null)}
            />
          </svg>

          {sel && indice != null && (
            <Rotulo
              izquierda={x(indice)}
              haciaLaIzquierda={x(indice) > (plot.x0 + plot.x1) / 2}
              titulo={hora(sel.t, true)}
              filas={[
                { color: COLOR_BAJADA, nombre: 'bajada', valor: velocidad(sel.downloadBps) },
                { color: COLOR_SUBIDA, nombre: 'subida', valor: velocidad(sel.uploadBps) },
              ]}
            />
          )}
        </>
      )}
    </Box>
  );
}

/** Una entrada de la leyenda: el trazo del color de la serie, su nombre y su último valor.
 *  El texto va en el color del texto, nunca en el de la serie: el color lo lleva el trazo. */
function Leyenda({ color, nombre, valor }: { color: string; nombre: string; valor: string }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 18, height: 0, borderTop: `2px solid ${color}`, borderRadius: 1 }} />
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>{valor}</Box>
        <Box component="span" sx={{ color: 'text.secondary' }}> {nombre}</Box>
      </Typography>
    </Stack>
  );
}

function Rotulo({
  izquierda, haciaLaIzquierda, titulo, filas,
}: {
  izquierda: number;
  haciaLaIzquierda: boolean;
  titulo: string;
  filas: { color: string; nombre: string; valor: string }[];
}) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: MARGEN.arriba + 4,
        left: haciaLaIzquierda ? undefined : izquierda + 12,
        right: haciaLaIzquierda ? `calc(100% - ${izquierda - 12}px)` : undefined,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        pointerEvents: 'none',
        minWidth: 150,
        boxShadow: 3,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{titulo}</Typography>
      {filas.map((f) => (
        <Stack key={f.nombre} direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 14, height: 0, borderTop: `2px solid ${f.color}` }} />
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{f.valor}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}> {f.nombre}</Box>
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

/** La misma serie, en filas: es lo que lee un lector de pantalla y lo que se copia a
 *  cualquier otro sitio. La más reciente arriba, que es la que se busca. */
function TablaPuntos({ puntos, conDia }: { puntos: SpeedPoint[]; conDia: boolean }) {
  return (
    <Box sx={{ maxHeight: ALTO, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Instante</TableCell>
            <TableCell align="right">Bajada</TableCell>
            <TableCell align="right">Subida</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...puntos].reverse().map((p) => (
            <TableRow key={p.t}>
              <TableCell>{hora(p.t, conDia)}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{velocidad(p.downloadBps)}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{velocidad(p.uploadBps)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
